/** @jsxImportSource @emotion/react */
import { FormProvider, useController, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction, DeleteButton } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { css } from "@emotion/react";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import {
  KioskConfiguration,
  KioskConfigurationFormValues,
} from "../models/kiosk-configuration";
import {
  useCreateKioskConfiguration,
  useUpdateKioskConfiguration,
  useUploadConfigImage,
} from "../service/kiosk-configuration-api";
import { ColorPickerInput } from "../../../components/forms/inputs/ColorPickerInput";
import { useEffect, useState } from "react";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import { DrawerSubHeader } from "../../../components/DrawerSubHeader";
import { StyledText } from "../../../assets/typography/typography";
import { BaseUploadPreview } from "../../../components/BaseUploadPreview";
import { AddFileSelector } from "../../../components/AddFileSelector";
import { colors } from "../../../components/colors";
import { getErrorMessage } from "../../../error-handler";
import { SwitchInput } from "../../../components/forms/inputs/SwitchInput";
import { FileUploadFormat } from "../../../components/forms/inputs/FileUploadInput";
import { SelectProductInput } from "../../../components/forms/inputs/SelectProductInput";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  kioskConfiguration?: KioskConfiguration;
};

const mutateUploadWrapperStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const additionalFormStyles = css`
  gap: 16px;
`;

const validationSchema = yup.object({
  name: yup
    .string()
    .required("Enter name of the kiosk configuration to continue"),
  version: yup
    .string()
    .required("Enter version of the kiosk configuration to continue"),
  title: yup
    .string()
    .required("Enter title of the kiosk configuration to continue"),
});

const imageUploadSectionStyles = css`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

export const MutateKioskConfigurationDrawer = ({
  isOpen,
  onClose,
  kioskConfiguration,
}: Props) => {
  const [config, setConfig] = useState<KioskConfiguration["config"]>({});
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [isLoadingMutation, setIsLoadingMutation] = useState(false);

  const methods = useForm<KioskConfigurationFormValues>({
    defaultValues: {
      checkoutInputType: "none",
      customBrandPrimary: "",
      customBrandSecondary: "",
      customBrandSubtle: "",
      name: "",
      version: "",
      title: "",
      startImageUrl: "",
      retailOrderingKiosk: false,
      ageVerificationProductId: "",
      startVideoUrl: "",
      customHeaderUrl: "",
      startCategorySelectedUrl: "",
      venueLogoUrl: "",
      venueLogoWhiteUrl: "",
      staffOrderingKiosk: false,
    },
    resolver: yupResolver(validationSchema),
  });

  const {
    mutateAsync: createKioskConfiguration,
  } = useCreateKioskConfiguration();

  const {
    mutateAsync: updateKioskConfiguration,
  } = useUpdateKioskConfiguration();

  const { mutateAsync: uploadConfigImage } = useUploadConfigImage();

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (data: KioskConfigurationFormValues) => {
    setIsLoadingMutation(true);
    const {
      customHeaderFiles,
      venueLogoFiles,
      venueLogoWhiteFiles,
      startImageFiles,
      startVideoFiles,
      customHeaderUrl,
      venueLogoUrl,
      venueLogoWhiteUrl,
      startImageUrl,
      startVideoUrl,
      customBrandPrimary,
      customBrandSecondary,
      customBrandSubtle,
      startCategorySelectedUrl,
      startCategorySelectedFiles,
      ageVerificationProductId,
      retailOrderingKiosk,
      ...rest
    } = data;
    let customHeaderUrlUploaded = "";
    let venueLogoUrlUploaded = "";
    let venueLogoWhiteUrlUploaded = "";
    let startImageUrlUploaded = "";
    let startVideoUrlUploaded = "";
    let startCategorySelectedUrlUploaded = "";
    let id = kioskConfiguration?.id || "";
    let ageVerificationProductId_ =
      retailOrderingKiosk && !!ageVerificationProductId
        ? ageVerificationProductId
        : null;

    try {
      if (!kioskConfiguration) {
        id = await createKioskConfiguration({
          organizationKeyName,
          data: {
            ...rest,
            customBrandPrimary: customBrandPrimary || null,
            customBrandSecondary: customBrandSecondary || null,
            customBrandSubtle: customBrandSubtle || null,
            retailOrderingKiosk,
            ageVerificationProductId: ageVerificationProductId_,
            config,
          },
        });
      }
      if (customHeaderFiles?.length > 0) {
        customHeaderUrlUploaded = await uploadConfigImage({
          file: customHeaderFiles[0],
          organizationKeyName,
          id,
          apiRoute: "shop-self-service-configs",
          preExtType: "shop-self-service-header",
        });
      }
      if (venueLogoFiles?.length > 0) {
        venueLogoUrlUploaded = await uploadConfigImage({
          file: venueLogoFiles[0],
          organizationKeyName,
          id,
          apiRoute: "shop-self-service-configs",
          preExtType: "shop-self-service-venue-logo",
        });
      }
      if (venueLogoWhiteFiles?.length > 0) {
        venueLogoWhiteUrlUploaded = await uploadConfigImage({
          file: venueLogoWhiteFiles[0],
          organizationKeyName,
          id,
          apiRoute: "shop-self-service-configs",
          preExtType: "shop-self-service-venue-logo-white",
        });
      }
      if (startImageFiles?.length > 0) {
        startImageUrlUploaded = await uploadConfigImage({
          file: startImageFiles[0],
          organizationKeyName,
          id,
          apiRoute: "shop-self-service-configs",
          preExtType: "shop-self-service-start-image",
        });
      }
      if (startVideoFiles?.length > 0) {
        startVideoUrlUploaded = await uploadConfigImage({
          file: startVideoFiles[0],
          organizationKeyName,
          id,
          apiRoute: "shop-self-service-configs",
          preExtType: "shop-self-service-start-video",
        });
      }

      if (startCategorySelectedFiles?.length > 0) {
        startCategorySelectedUrlUploaded = await uploadConfigImage({
          file: startCategorySelectedFiles[0],
          organizationKeyName,
          id,
          apiRoute: "shop-self-service-configs",
          preExtType: "shop-self-service-start-category-selected-image",
        });
      }

      // If create and no files uploaded, return
      if (
        !kioskConfiguration &&
        !customHeaderUrlUploaded &&
        !venueLogoUrlUploaded &&
        !venueLogoWhiteUrlUploaded &&
        !startImageUrlUploaded &&
        !startVideoUrlUploaded &&
        !startCategorySelectedUrlUploaded
      ) {
        toastr.success("Success", `Kiosk configuration successfully created`);
        handleOnClose();
        setIsLoadingMutation(false);
        return;
      }

      // Otherwise, update
      await updateKioskConfiguration({
        organizationKeyName,
        data: {
          id,
          ...rest,
          retailOrderingKiosk,
          ageVerificationProductId: ageVerificationProductId_,
          customBrandPrimary: customBrandPrimary || null,
          customBrandSecondary: customBrandSecondary || null,
          customBrandSubtle: customBrandSubtle || null,
          customHeaderUrl: customHeaderUrlUploaded
            ? customHeaderUrlUploaded
            : customHeaderUrl
            ? customHeaderUrl
            : null,
          venueLogoUrl: venueLogoUrlUploaded
            ? venueLogoUrlUploaded
            : venueLogoUrl
            ? venueLogoUrl
            : null,
          venueLogoWhiteUrl: venueLogoWhiteUrlUploaded
            ? venueLogoWhiteUrlUploaded
            : venueLogoWhiteUrl
            ? venueLogoWhiteUrl
            : null,
          startImageUrl: startImageUrlUploaded
            ? startImageUrlUploaded
            : startImageUrl
            ? startImageUrl
            : null,
          startVideoUrl: startVideoUrlUploaded
            ? startVideoUrlUploaded
            : startVideoUrl
            ? startVideoUrl
            : null,
          startCategorySelectedUrl: startCategorySelectedUrlUploaded
            ? startCategorySelectedUrlUploaded
            : startCategorySelectedUrl
            ? startCategorySelectedUrl
            : null,
          config,
        },
      });
      toastr.success(
        "Success",
        `Kiosk configuration successfully ${
          kioskConfiguration?.id ? "updated" : "created"
        }`
      );
      handleOnClose();
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    } finally {
      setIsLoadingMutation(false);
    }
  };

  useEffect(() => {
    if (!kioskConfiguration) {
      return;
    }
    methods.reset({
      checkoutInputType: kioskConfiguration?.checkoutInputType || "",
      customBrandPrimary: kioskConfiguration?.customBrandPrimary || "",
      customBrandSecondary: kioskConfiguration?.customBrandSecondary || "",
      customBrandSubtle: kioskConfiguration?.customBrandSubtle || "",
      name: kioskConfiguration?.name || "",
      title: kioskConfiguration?.title || "",
      version: kioskConfiguration?.version || "1.0.0",
      startImageUrl: kioskConfiguration?.startImageUrl || "",
      ageVerificationProductId:
        kioskConfiguration?.ageVerificationProductId || "",
      retailOrderingKiosk: kioskConfiguration.retailOrderingKiosk,
      startVideoUrl: kioskConfiguration?.startVideoUrl || "",
      startCategorySelectedUrl:
        kioskConfiguration?.startCategorySelectedUrl || "",
      customHeaderUrl: kioskConfiguration?.customHeaderUrl || "",
      venueLogoUrl: kioskConfiguration?.venueLogoUrl || "",
      venueLogoWhiteUrl: kioskConfiguration?.venueLogoWhiteUrl || "",
      staffOrderingKiosk: kioskConfiguration?.staffOrderingKiosk || false,
    });
    // Empty object is parsed as array by PHP, so we need to check for that
    setConfig(
      Array.isArray(kioskConfiguration.config) ? {} : kioskConfiguration.config
    );
  }, [kioskConfiguration, methods]);

  const startImageUrl = methods.watch("startImageUrl");
  const startVideoUrl = methods.watch("startVideoUrl");
  const customHeaderUrl = methods.watch("customHeaderUrl");
  const venueLogoUrl = methods.watch("venueLogoUrl");
  const venueLogoWhiteUrl = methods.watch("venueLogoWhiteUrl");
  const startCategorySelectedUrl = methods.watch("startCategorySelectedUrl");

  const customHeaderFiles = methods.watch("customHeaderFiles");
  const venueLogoFiles = methods.watch("venueLogoFiles");
  const venueLogoWhiteFiles = methods.watch("venueLogoWhiteFiles");
  const startImageFiles = methods.watch("startImageFiles");
  const startVideoFiles = methods.watch("startVideoFiles");
  const startCategorySelectedFiles = methods.watch(
    "startCategorySelectedFiles"
  );

  const {
    field: { onChange: onChangeCustomHeaderFiles },
  } = useController({
    name: "customHeaderFiles",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeCustomHeaderUrl },
  } = useController({
    name: "customHeaderUrl",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeVenueLogoFiles },
  } = useController({
    name: "venueLogoFiles",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeVenueLogoUrl },
  } = useController({
    name: "venueLogoUrl",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeVenueLogoWhiteFiles },
  } = useController({
    name: "venueLogoWhiteFiles",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeVenueLogoWhiteUrl },
  } = useController({
    name: "venueLogoWhiteUrl",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeStartImageFiles },
  } = useController({
    name: "startImageFiles",
    control: methods.control,
  });
  const {
    field: { onChange: onChangeStartImageUrl },
  } = useController({
    name: "startImageUrl",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeStartVideoFiles },
  } = useController({
    name: "startVideoFiles",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeStartVideoUrl },
  } = useController({
    name: "startVideoUrl",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeStartCategorySelectedFiles },
  } = useController({
    name: "startCategorySelectedFiles",
    control: methods.control,
  });

  const {
    field: { onChange: onChangeStartCategorySelectedUrl },
  } = useController({
    name: "startCategorySelectedUrl",
    control: methods.control,
  });

  const UploadSection = ({
    label,
    type,
    formUrl,
    formFiles,
    accept,
    onDelete,
    onSave,
    subtext,
  }: {
    label: string;
    type: "image" | "video";
    formUrl: string;
    formFiles: File[];
    accept: FileUploadFormat;
    onDelete: () => void;
    onSave: (files: File[]) => void;
    subtext: string;
  }) => {
    return (
      <div css={imageUploadSectionStyles}>
        <StyledText
          className="bodySubtextSemibold mb-0"
          color={colors.textPrimary}
        >
          {label}
        </StyledText>
        <StyledText
          className="bodySubtextRegular mb-0"
          color={colors.textSecondary}
        >
          {subtext}
        </StyledText>
        {(formFiles?.length > 0 || formUrl) && (
          <BaseUploadPreview
            url={
              formFiles?.length > 0
                ? URL.createObjectURL(formFiles[0])
                : formUrl
            }
            type={type}
          />
        )}
        <div css={mutateUploadWrapperStyle}>
          <AddFileSelector
            label={label}
            accept={accept}
            onSubmit={async (files) => {
              onSave(files);
            }}
          />
          {(formFiles?.length > 0 || formUrl) && (
            <DeleteButton onClick={onDelete}>Delete</DeleteButton>
          )}
        </div>
      </div>
    );
  };

  return (
    <Drawer
      title={`${
        kioskConfiguration?.id ? "Edit" : "Create"
      } kiosk configuration`}
      width={500}
      buttons={
        <ButtonAction
          disabled={isLoadingMutation}
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
        >
          {isLoadingMutation ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <StyledForm additionalStyles={additionalFormStyles}>
        <FormProvider {...methods}>
          <TextInput
            label="Kiosk configuration name"
            name="name"
            placeholder="Enter name"
            helperText="Only visible to admins."
          />
          <TextInput
            label="Kiosk configuration title"
            name="title"
            placeholder="Enter title"
          />
          <TextInput
            label="Kiosk configuration version"
            name="version"
            placeholder="Enter version"
            helperText="This is used to identify the version of the kiosk configuration. It should be in the format of X.X.X where X is a number."
          />
          <ColorPickerInput
            label="Custom brand primary"
            name="customBrandPrimary"
            placeholder="#000000"
            helperText="This will be used for buttons and other Call To Action elements. Should be a dark color that contrasts well with white."
          />
          <ColorPickerInput
            label="Custom brand secondary"
            name="customBrandSecondary"
            placeholder="#000000"
            helperText="This will be used bottom bar of the Windows Kiosk. Usually a medium color."
          />
          <ColorPickerInput
            label="Custom brand subtle"
            name="customBrandSubtle"
            placeholder="#000000"
            helperText="This is usually a light color. Mainly elements with color Custom Brand Primary will be placed above this."
          />
          <SelectInput
            label="Checkout input type"
            name="checkoutInputType"
            options={[
              { value: "none", label: "None" },
              { value: "buzzer", label: "Buzzer" },
              { value: "name", label: "Name" },
            ]}
            helperText="Choose 'Buzzer' if you want to enable entering buzzer number on kiosk. Choose 'Name' if users should enter their name on kiosk. Choose none if you want none of them."
          />
          <SwitchInput
            label="Should it be possible to place orders without paying through HeapsGo? Only allow if you process payments through OnlinePOS."
            name="staffOrderingKiosk"
          />
          <SwitchInput
            label="Product scanning is enabled."
            name="retailOrderingKiosk"
          />
          {methods.watch("retailOrderingKiosk") && (
            <SelectProductInput
              name="ageVerificationProductId"
              classification="generic"
              label="Age verification product"
              helperText="If product scanning is enabled, this product will be used to verify the age of the customer, if they are buying products that require age verification."
            />
          )}
        </FormProvider>
        <UploadSection
          label="Custom header Image"
          type="image"
          subtext="This is only relevant if you are configuring a Windows Kiosk. It is the element shown in the top of all pages."
          formUrl={customHeaderUrl}
          formFiles={customHeaderFiles}
          accept={FileUploadFormat.IMAGE}
          onDelete={() => {
            onChangeCustomHeaderFiles([]);
            onChangeCustomHeaderUrl("");
          }}
          onSave={onChangeCustomHeaderFiles}
        />
        <UploadSection
          label="Venue logo Image"
          subtext="The logo of the brand in colors"
          type="image"
          formUrl={venueLogoUrl}
          formFiles={venueLogoFiles}
          accept={FileUploadFormat.IMAGE}
          onDelete={() => {
            onChangeVenueLogoFiles([]);
            onChangeVenueLogoUrl("");
          }}
          onSave={onChangeVenueLogoFiles}
        />
        <UploadSection
          label="Venue logo Image (White)"
          subtext="The logo of the brand in white"
          type="image"
          formUrl={venueLogoWhiteUrl}
          formFiles={venueLogoWhiteFiles}
          accept={FileUploadFormat.IMAGE}
          onDelete={() => {
            onChangeVenueLogoWhiteFiles([]);
            onChangeVenueLogoWhiteUrl("");
          }}
          onSave={onChangeVenueLogoWhiteFiles}
        />
        <UploadSection
          label="Start image"
          subtext="Image used on the start screen of the kiosk. If you upload a video below, this will never show."
          type="image"
          formUrl={startImageUrl}
          formFiles={startImageFiles}
          accept={FileUploadFormat.IMAGE}
          onDelete={() => {
            onChangeStartImageFiles([]);
            onChangeStartImageUrl("");
          }}
          onSave={onChangeStartImageFiles}
        />
        <UploadSection
          label="Start video"
          subtext="Video used on the start screen of the kiosk."
          type="video"
          formUrl={startVideoUrl}
          formFiles={startVideoFiles}
          accept={FileUploadFormat.VIDEO}
          onDelete={() => {
            onChangeStartVideoFiles([]);
            onChangeStartVideoUrl("");
          }}
          onSave={onChangeStartVideoFiles}
        />
        <UploadSection
          label="Start category selected image"
          subtext="Image used for the start button on the kiosk."
          type="image"
          formUrl={startCategorySelectedUrl}
          formFiles={startCategorySelectedFiles}
          accept={FileUploadFormat.IMAGE}
          onDelete={() => {
            onChangeStartCategorySelectedFiles([]);
            onChangeStartCategorySelectedUrl("");
          }}
          onSave={onChangeStartCategorySelectedFiles}
        />
        <DrawerSubHeader
          header="Configuration"
          subtext="The below is used for configuration that is not standard. Contact HeapsGo before editing."
        />
        <JsonView
          src={config}
          editable
          css={css`
            width: 100%;
          `}
        />
      </StyledForm>
    </Drawer>
  );
};
